import React, { FC, ReactNode, useMemo } from 'react';

import { BaseInput } from './BaseInput';

import { cn } from '@/shared/lib/tailwind-merge';

export enum InputColor {
    Default = 'default',
    Primary = 'primary',
    Secondary = 'secondary',
    Warning = 'warning',
    Error = 'error',
}

export enum InputSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

export type InputProps = React.ComponentProps<typeof BaseInput> & {
    color?: InputColor;
    size?: InputSize;
    name?: string;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    disabled?: boolean;
    className?: string;
};

interface InputComponent extends FC<InputProps> {
    colors: typeof InputColor;
    sizes: typeof InputSize;
}

const Input: InputComponent = ({
    leftIcon,
    rightIcon,
    color = InputColor.Default,
    size = InputSize.Medium,
    disabled = false,
    name,
    className,
    ...props
}) => {
    const colorClasses = {
        [InputColor.Default]: 'border-border-opaque text-content-fourth font-medium',
        [InputColor.Primary]: 'border-2 border-border-accent1 text-content-fourth font-medium',
        [InputColor.Secondary]: 'border-border-selected text-content-fourth font-medium',
        [InputColor.Warning]: 'border-border-warning text-content-fourth font-medium',
        [InputColor.Error]: 'border-border-negative text-content-fourth font-medium',
    };

    const inputSizeClasses = {
        [InputSize.Small]: 'h-[36px] text-md',
        [InputSize.Medium]: 'h-[40px] text-md',
        [InputSize.Large]: 'h-[46px] text-lg',
    };

    const iconClasses = {
        [InputSize.Small]: 'h-[36px] w-[36px] px-3 flex items-center justify-center',
        [InputSize.Medium]: 'h-[44px] w-[44px] px-3 flex items-center justify-center',
        [InputSize.Large]: 'h-[46px] w-[46px] px-3 flex items-center justify-center',
    };

    const leftIconSpaces = {
        [InputSize.Small]: 'pl-9',
        [InputSize.Medium]: 'pl-10',
        [InputSize.Large]: 'pl-10',
    };

    const rightIconSpaces = {
        [InputSize.Small]: 'pr-9',
        [InputSize.Medium]: 'pr-10',
        [InputSize.Large]: 'pr-10',
    };

    const testId = useMemo(() => {
        let testId = '';

        if (name) {
            testId = name.toLowerCase().replace(/\s/g, '-');
            return testId + '-input';
        }

        return `${size}-input`;
    }, [name]);

    return (
        <div
            className={cn({
                'cursor-not-allowed': disabled,
                'opacity-50': disabled,
            })}
        >
            <div className='relative'>
                {leftIcon && (
                    <div
                        className={cn(
                            'absolute left-2 top-1/2 transform -translate-y-1/2',
                            iconClasses[size],
                            {
                                'opacity-50': disabled,
                            },
                        )}
                    >
                        {leftIcon}
                    </div>
                )}
                <BaseInput
                    {...props}
                    data-testid={testId}
                    className={cn(
                        'w-full px-4 py-2 border rounded-md box-border shadow-none',
                        inputSizeClasses[size],
                        colorClasses[color],
                        leftIcon ? leftIconSpaces[size] : '',
                        rightIcon ? rightIconSpaces[size] : '',
                        className,
                    )}
                />
                {rightIcon && (
                    <div
                        className={cn(
                            'absolute inset-y-0 right-2 flex items-center h-full w-[44px] justify-center',
                            iconClasses[size],
                            {
                                'opacity-50': disabled,
                            },
                        )}
                    >
                        {rightIcon}
                    </div>
                )}
            </div>
        </div>
    );
};

Input.colors = InputColor;
Input.sizes = InputSize;

export { Input };
